import { cn } from '@/lib/utils'

export interface SymbolLabelParts {
  /** Always shown: the base asset ("BTC-"). */
  head: string
  /** Truncated first when the tab is narrow; empty when the label ends-truncates instead. */
  middle: string
  /** Always shown: what tells siblings apart ("-100000-C", "-PERP"). */
  tail: string
}

/**
 * Splits a tab's symbol so a narrow tab keeps both ends. End truncation turned every
 * perpetual into "BTC-PERPETU…" and every option on one expiry into "BTC-27DEC24-…",
 * hiding exactly the part that differs between tabs. "-PERPETUAL" is compacted to "-PERP"
 * (the tab's hover card still shows the full name), and for three or more segments the
 * middle ones give way first, keeping the base and the last segment (strike and type for
 * an option).
 */
export function splitSymbolLabel(text: string): SymbolLabelParts {
  const compact = text.replace(/-PERPETUAL$/i, '-PERP')
  const parts = compact.split('-')
  if (parts.length < 3 || parts.some((part) => !part)) {
    return { head: compact, middle: '', tail: '' }
  }
  const isOption = /^[CP]$/i.test(parts[parts.length - 1])
  const tailCount = isOption && parts.length >= 4 ? 2 : 1
  return {
    head: `${parts[0]}-`,
    middle: parts.slice(1, -tailCount).join('-'),
    tail: `-${parts.slice(-tailCount).join('-')}`,
  }
}

/** A widget tab's instrument label, truncated in the middle (see splitSymbolLabel). */
export function TabSymbolLabel({ text, className }: { text: string; className?: string }) {
  const { head, middle, tail } = splitSymbolLabel(text)
  return (
    <span
      className={cn(
        'flex min-w-0 text-sm font-semibold tracking-tight whitespace-nowrap',
        className
      )}
    >
      {middle ? (
        <>
          <span className="shrink-0">{head}</span>
          <span className="min-w-3 truncate">{middle}</span>
          <span className="shrink-0">{tail}</span>
        </>
      ) : (
        <span className="truncate">{head}</span>
      )}
    </span>
  )
}
